import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, Clock, Loader2 } from "lucide-react";
import { AuctionClose, closeShort } from "@/lib/brand";
import { api } from "@/lib/api";
import { getApplication } from "@/lib/applyApi";
import { fmtD } from "@/lib/pipelineApi";
import { Button } from "@/components/ui/button";
import { SaleScheduling } from "@/components/SaleScheduling";
import { iso, toDate } from "@/components/adtemplate/DatePick";
import { AdDatesFields, computeDates, minusWeeks, startOverride, fetchStart } from "@/components/schedule/AdDates";

const to24 = (l) => { const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec((l || "").trim()); if (!m) return ""; let h = Number(m[1]) % 12; if (m[3].toUpperCase() === "PM") h += 12; return `${String(h).padStart(2, "0")}:${m[2]}`; };
const to12 = (t) => { if (!t) return ""; const [h, m] = t.split(":").map(Number); return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`; };

const weeksNum = (x) => { const n = parseInt(String(x ?? "").trim(), 10); return n >= 1 && n <= 52 ? n : null; };

// Imported (Smartsheet) horses have no application schedule: edit the horse's existing sale date fields (both copies kept in step).
function HorseSchedule({ horseId, onChanged }) {
  const [h, setH] = useState(null);
  const [v, setV] = useState({});
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState({});
  const [cw, setCw] = useState(null);
  const load = () => api.get(`/horses/${horseId}`).then(async ({ data }) => {
    const si = data.sale_info || {}, hi = data.horse_info || {};
    const contractW = weeksNum((data.internal_info || {}).ad_weeks);
    const close = hi.sale_close_date || si.sale_close_date, start = hi.sale_start_date || si.sale_start_date, fb = si.facebook_post_date || si.preview_start_date || hi.preview_start_date;
    const w = contractW || weeksNum(si.schedule_weeks);
    const autoPost = close && w ? minusWeeks(close, w) : null;
    await fetchStart(close);
    const s = { close: toDate(close), weeks: si.schedule_weeks ? String(si.schedule_weeks) : "",
      post: fb && fb !== autoPost ? toDate(fb) : undefined, ...startOverride(close, start, to24(si.sale_start_time)) };
    setH(data); setCw(contractW); setV(s); setSaved(s);
  });
  useEffect(() => { load().catch(() => setH({})); }, [horseId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!h) return <Loader2 className="h-4 w-4 animate-spin text-stone-400" />;
  const d = computeDates(v, cw);
  const key = (s) => [s.close && iso(s.close), s.post && iso(s.post), s.start && iso(s.start), s.stime, s.weeks].join("|");
  const dirty = key(v) !== key(saved);
  const save = async () => {
    setBusy(true);
    const dates = { sale_start_date: d.startDate, sale_close_date: d.close, sale_close_time: "" };
    try {
      await api.patch(`/horses/${horseId}`, {
        horse_info: dates,
        sale_info: { ...dates, sale_start_time: to12(d.startTime), sale_time_zone: "MT", facebook_post_date: d.post || "", ...(cw ? {} : { schedule_weeks: d.weeks ? String(d.weeks) : "" }) },
      });
      await load();
      toast.success("Sale schedule saved");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save the schedule"); }
    setBusy(false);
  };
  return (
    <div data-testid="horse-schedule-form" className="space-y-2">
      <AdDatesFields v={v} setV={setV} contractWeeks={cw} weeksSource="Smartsheet “Ad # Weeks”" testPrefix="horse-sched" calendarMode={h?.is_test ? "test" : "live"} />
      <Button data-testid="horse-sched-save" onClick={save} disabled={busy || d.invalid || !dirty} className="w-full h-11 rounded-full bg-amber-800 hover:bg-amber-900 text-white">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Schedule"}</Button>
    </div>
  );
}

function AppSchedule({ appId, onChanged }) {
  const [app, setApp] = useState(null);
  useEffect(() => { getApplication(appId).then(setApp).catch(() => setApp(false)); }, [appId]);
  if (app === null) return <Loader2 className="h-4 w-4 animate-spin text-stone-400" />;
  if (!app) return <p className="text-sm text-stone-600">Could not load the application schedule.</p>;
  return <SaleScheduling app={app} onChange={(a) => { setApp(a); onChanged?.(); }} />;
}

export function ScheduleTab({ c, canEdit, onChanged }) {
  const appId = c.ids.application_id, hid = c.ids.horse_id;
  if (!canEdit) return <p className="text-sm text-stone-600">Only admins can edit the sale schedule.</p>;
  if (appId) return <div data-testid="workspace-schedule"><AppSchedule appId={appId} onChanged={onChanged} /></div>;
  return (
    <section data-testid="workspace-schedule" className="bg-white border border-stone-200 rounded-xl p-4">
      <h2 className="font-semibold text-stone-900 mb-1 flex items-center gap-2"><CalendarDays className="h-4 w-4 text-stone-600" /> Sale Scheduling</h2>
      <p className="text-[11px] text-stone-400 mb-3 flex items-center gap-1"><Clock className="h-3 w-3" /> Bidding closes <AuctionClose />. Saving never publishes or unpublishes the ad.</p>
      <HorseSchedule horseId={hid} onChanged={onChanged} />
    </section>
  );
}

export function ScheduleSummary({ c, canEdit, onEdit }) {
  const d = c.dates || {};
  const has = d.bidding_starts || d.close_date;
  return (
    <div data-testid="workspace-schedule-summary" className="w-full sm:w-auto shrink-0 text-left sm:text-right text-xs sm:text-sm space-y-0.5 rounded-lg sm:rounded-none bg-white sm:bg-transparent border border-stone-200 sm:border-0 p-2 sm:p-0">
      {has ? (
        <>
          {d.facebook_post_date && <p data-testid="workspace-posting-date"><span className="text-stone-500">Posting</span> <span className="font-semibold text-stone-900">{fmtD(d.facebook_post_date)}</span></p>}
          <p data-testid="workspace-ad-duration" className="text-stone-500">{d.ad_weeks ? `Advertising Duration: ${d.ad_weeks} Week${d.ad_weeks === 1 ? "" : "s"}` : "Advertising Duration Not Set"}</p>
          <p data-testid="workspace-bidding-starts"><span className="text-stone-500">Bidding Starts</span> <span className="font-semibold text-stone-900">{d.bidding_starts ? `${fmtD(d.bidding_starts)}${d.bidding_starts_time ? ` · ${d.bidding_starts_time.replace(" Mountain Time", " MT")}` : ""}` : "—"}</span></p>
          <p data-testid="workspace-bidding-ends"><span className="text-stone-500">Bidding Ends</span> <span className="font-semibold text-stone-900">{d.close_date ? `${fmtD(d.close_date)} · ${closeShort(d.close_time)}` : "—"}</span></p>
        </>
      ) : <p data-testid="workspace-not-scheduled" className="font-semibold text-stone-600">Not Scheduled</p>}
      {canEdit && (c.ids.horse_id || c.ids.application_id) && <button type="button" data-testid="workspace-edit-schedule" onClick={onEdit} className="text-xs font-semibold text-amber-800 underline">Edit Schedule</button>}
    </div>
  );
}
